import {
  ArrowBack,
  ArrowForward,
  Check,
  CheckCircle,
  Close,
  CloudSync,
  CloudUpload,
  Description,
  Edit,
  EditNote,
  Elderly,
  Groups,
  HomeWork,
  LocationOn,
  RocketLaunch,
  TabletMac,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import type { ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslate } from "react-admin";

import { LocationTypeEnum } from "../../generated/graphql";
import { captureException } from "../../lib/errorHandling";
import { borderRadius, color, sessionStatusStyle, shadows, space, typography } from "../../theme/designTokens";
import { IntegrationSettings } from "../IntegrationSettings";
import {
  createManualLocationDraft,
  createManualPatientDraft,
  createManualPractitionerDraft,
  type ExistingRecordsByResource,
  getManualLocationTotals,
  isManualLocationDraftFilled,
  isManualLocationDraftValid,
  isManualPatientDraftFilled,
  isManualPatientDraftValid,
  isManualPractitionerDraftFilled,
  isManualPractitionerDraftValid,
  isManualWardDraftFilled,
  type ManualLocationChoice,
  type ManualLocationDraft,
  ManualLocationsEditor,
  type ManualPatientDraft,
  ManualPatientsEditor,
  type ManualPractitionerDraft,
  ManualPractitionersEditor,
  type ManualSetupSubmission,
} from "./ManualSetupEditors";
import {
  createDefaultMappings,
  type CreateResource,
  type FieldMappings,
  importPreviewRecords,
  type ImportRecordKind,
  mappingFields,
  type OnboardingPath,
} from "./simulationData";

type WizardStep =
  | "welcome"
  | "method"
  | "integration"
  | "file"
  | "mapping"
  | "location"
  | "practitioner"
  | "patient"
  | "preview"
  | "result";

export type OnboardingWizardCounts = {
  locationCount: number;
  practitionerCount: number;
  patientCount: number;
};

type OnboardingWizardProps = {
  counts: OnboardingWizardCounts;
  existingRecords: ExistingRecordsByResource;
  locationChoices: ManualLocationChoice[];
  onClose: () => void;
  onOpenDeviceEnrollment: () => void;
  onSubmitManualSetup: (submission: ManualSetupSubmission) => Promise<void>;
  open: boolean;
};

type MethodCardProps = {
  action: string;
  description: string;
  icon: ReactNode;
  onSelect: () => void;
  points: string[];
  title: string;
};

const wizardSequences: Record<OnboardingPath, WizardStep[]> = {
  file: ["welcome", "method", "file", "mapping", "preview", "result"],
  integration: ["welcome", "method", "integration"],
  manual: ["welcome", "method", "location", "practitioner", "patient", "preview", "result"],
};

const kindTranslationKeys: Record<ImportRecordKind, string> = {
  location: "onboarding.preview.groups.locations",
  practitioner: "onboarding.preview.groups.practitioners",
  patient: "onboarding.preview.groups.patients",
};

const MethodCard = ({ action, description, icon, onSelect, points, title }: MethodCardProps) => (
  <Card
    variant="outlined"
    sx={{
      borderColor: color.panelBorder,
      borderRadius: borderRadius.md,
      boxShadow: shadows.card,
      display: "flex",
      flexDirection: "column",
      height: "100%",
    }}
  >
    <CardContent sx={{ display: "flex", flexDirection: "column", flexGrow: 1, gap: space.xs }}>
      <Box
        sx={{
          alignItems: "center",
          backgroundColor: color.selectedOptionBackground,
          borderRadius: borderRadius.md,
          color: color.primary,
          display: "flex",
          height: 44,
          justifyContent: "center",
          width: 44,
        }}
      >
        {icon}
      </Box>
      <Typography variant="h6">{title}</Typography>
      <Typography color="text.secondary" variant="body2">
        {description}
      </Typography>
      <Stack spacing={space.xxs} sx={{ mt: "auto" }}>
        {points.map(point => (
          <Box key={point} sx={{ alignItems: "flex-start", display: "flex", gap: space.xxs }}>
            <Check color="success" fontSize="small" />
            <Typography color="text.secondary" variant="body2">
              {point}
            </Typography>
          </Box>
        ))}
      </Stack>
    </CardContent>
    <CardActions sx={{ p: space.xs, pt: 0 }}>
      <Button fullWidth onClick={onSelect} variant="contained">
        {action}
      </Button>
    </CardActions>
  </Card>
);

type ProgressStripProps = {
  currentStep: WizardStep;
  sequence: WizardStep[];
};

const ProgressStrip = ({ currentStep, sequence }: ProgressStripProps) => {
  const translate = useTranslate();
  const lastStepIsResult = sequence[sequence.length - 1] === "result";
  const progressSteps = lastStepIsResult ? sequence.slice(2, -1) : sequence.slice(2);
  const currentIndex = progressSteps.indexOf(currentStep);

  if (currentIndex < 0) {
    return null;
  }

  return (
    <Box
      aria-label={translate("onboarding.wizard.progress")}
      sx={{
        borderBottom: `1px solid ${color.panelBorder}`,
        display: "flex",
        gap: space.xs,
        px: space.sm,
        pb: space.xs,
      }}
    >
      {progressSteps.map((step, index) => {
        const complete = index < currentIndex;
        const active = index === currentIndex;
        return (
          <Box key={step} sx={{ display: "flex", flex: 1, flexDirection: "column", gap: space.xxs, minWidth: 0 }}>
            <Box
              sx={{
                backgroundColor: complete || active ? color.primary : color.progressTrack,
                borderRadius: borderRadius.lg,
                height: 5,
              }}
            />
            <Box sx={{ alignItems: "center", display: "flex", gap: space.xxs }}>
              <Box
                sx={{
                  alignItems: "center",
                  backgroundColor: complete || active ? color.primary : color.progressTrack,
                  borderRadius: borderRadius.lg,
                  color: complete || active ? color.white : color.secondary40,
                  display: "flex",
                  flexShrink: 0,
                  height: 24,
                  justifyContent: "center",
                  width: 24,
                }}
              >
                {complete ? <Check fontSize="small" /> : index + 1}
              </Box>
              <Typography
                color={active ? "text.primary" : "text.secondary"}
                fontWeight={active ? "medium" : "normal"}
                noWrap
                sx={{ display: { xs: "none", md: "block" } }}
                variant="body2"
              >
                {translate(`onboarding.wizard.steps.${step}`)}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

const uppercaseLabelSx = {
  color: color.secondary40,
  fontSize: typography.fontSize.xxs,
  fontWeight: typography.fontWeight.semibold,
  letterSpacing: typography.letterSpacing.md,
  textTransform: "uppercase",
} as const;

const manualResources: Array<{ icon: ReactNode; resource: CreateResource }> = [
  { icon: <HomeWork />, resource: "location" },
  { icon: <Groups />, resource: "practitioner" },
  { icon: <Elderly />, resource: "patient" },
];

type ResultStat = {
  color: string;
  icon: ReactNode;
  key: "sites" | "wards" | "locations" | "practitioners" | "patients";
  value: number;
};

const countRecords = (selectedIds: Set<string>, kind: ImportRecordKind) =>
  importPreviewRecords.filter(record => record.kind === kind && selectedIds.has(record.id)).length;

export const OnboardingWizard = ({
  counts,
  existingRecords,
  locationChoices,
  onClose,
  onOpenDeviceEnrollment,
  onSubmitManualSetup,
  open,
}: OnboardingWizardProps) => {
  const translate = useTranslate();
  const countsRef = useRef(counts);
  countsRef.current = counts;
  const [path, setPath] = useState<OnboardingPath | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [selectedFile, setSelectedFile] = useState("");
  const [mappings, setMappings] = useState<FieldMappings | null>(null);
  const [selectedRecordIds, setSelectedRecordIds] = useState(
    () => new Set(importPreviewRecords.map(record => record.id))
  );
  const [locationDrafts, setLocationDrafts] = useState<ManualLocationDraft[]>(() => [createManualLocationDraft()]);
  const [practitionerDrafts, setPractitionerDrafts] = useState<ManualPractitionerDraft[]>(() => [
    createManualPractitionerDraft(),
  ]);
  const [patientDrafts, setPatientDrafts] = useState<ManualPatientDraft[]>(() => [createManualPatientDraft()]);
  const [manualBaselineCounts, setManualBaselineCounts] = useState(counts);
  const existingWardCount = locationChoices.filter(choice => choice.type === LocationTypeEnum.Ward).length;
  const existingWardCountRef = useRef(existingWardCount);
  existingWardCountRef.current = existingWardCount;
  const [manualBaselineWardCount, setManualBaselineWardCount] = useState(existingWardCount);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);

  const sequence = path ? wizardSequences[path] : (["welcome", "method"] as WizardStep[]);
  const currentStep = sequence[stepIndex] ?? "welcome";
  const lastStepIsResult = sequence[sequence.length - 1] === "result";

  useEffect(() => {
    if (!open) {
      return;
    }

    setPath(null);
    setStepIndex(0);
    setSelectedFile("");
    setMappings(null);
    setSelectedRecordIds(new Set(importPreviewRecords.map(record => record.id)));
    setLocationDrafts([createManualLocationDraft()]);
    setPractitionerDrafts([createManualPractitionerDraft()]);
    setPatientDrafts([createManualPatientDraft()]);
    setManualBaselineCounts(countsRef.current);
    setManualBaselineWardCount(existingWardCountRef.current);
    setIsSaving(false);
    setSaveError(false);
  }, [open]);

  const selectPath = (selectedPath: OnboardingPath) => {
    setPath(selectedPath);
    setStepIndex(2);
    setMappings(selectedPath === "file" ? createDefaultMappings() : null);
  };

  const requiredMappingsComplete =
    mappings !== null && mappingFields.filter(field => field.required).every(field => !!mappings[field.key]);
  const mappedFieldCount = mappings ? Object.values(mappings).filter(Boolean).length : 0;
  const selectedRecordCount = selectedRecordIds.size;

  const manualStepCounts: Record<CreateResource, number> = {
    location: counts.locationCount,
    practitioner: counts.practitionerCount,
    patient: counts.patientCount,
  };
  const filledLocationDrafts = useMemo(
    () =>
      locationDrafts
        .filter(isManualLocationDraftFilled)
        .map(draft => ({ ...draft, wards: draft.wards.filter(isManualWardDraftFilled) })),
    [locationDrafts]
  );
  const filledPractitionerDrafts = practitionerDrafts.filter(isManualPractitionerDraftFilled);
  const filledPatientDrafts = patientDrafts.filter(isManualPatientDraftFilled);
  const locationTotals = getManualLocationTotals(locationDrafts);
  const manualDraftCounts: Record<CreateResource, number> = {
    location: locationTotals.sites,
    practitioner: filledPractitionerDrafts.length,
    patient: filledPatientDrafts.length,
  };
  const manualResultCounts: Record<CreateResource, number> = {
    location: Math.max(counts.locationCount, manualBaselineCounts.locationCount + manualDraftCounts.location),
    practitioner: Math.max(
      counts.practitionerCount,
      manualBaselineCounts.practitionerCount + manualDraftCounts.practitioner
    ),
    patient: Math.max(counts.patientCount, manualBaselineCounts.patientCount + manualDraftCounts.patient),
  };
  const manualResultWardCount = Math.max(existingWardCount, manualBaselineWardCount + locationTotals.wards);
  const stagedLocationChoices = useMemo<ManualLocationChoice[]>(
    () =>
      filledLocationDrafts.flatMap(draft => [
        {
          id: draft.id,
          name: draft.name,
          type: LocationTypeEnum.Site,
        },
        ...draft.wards.map(ward => ({
          id: ward.id,
          name: ward.name,
          partOfId: draft.id,
          type: LocationTypeEnum.Ward,
        })),
      ]),
    [filledLocationDrafts]
  );
  const availableLocationChoices = useMemo(() => {
    const choicesById = new Map<string, ManualLocationChoice>();
    [...locationChoices, ...stagedLocationChoices].forEach(choice => choicesById.set(choice.id, choice));
    return Array.from(choicesById.values());
  }, [locationChoices, stagedLocationChoices]);

  // A step can continue when every touched row is complete and the portal ends
  // up with at least one record of that kind (already saved or staged).
  const manualStepCanContinue = (resource: CreateResource) => {
    const hasRecords = manualDraftCounts[resource] > 0 || manualStepCounts[resource] > 0;
    switch (resource) {
      case "location":
        return hasRecords && filledLocationDrafts.every(isManualLocationDraftValid);
      case "practitioner":
        return (
          hasRecords &&
          filledPractitionerDrafts.every(draft => isManualPractitionerDraftValid(draft, availableLocationChoices))
        );
      case "patient":
        return (
          hasRecords && filledPatientDrafts.every(draft => isManualPatientDraftValid(draft, availableLocationChoices))
        );
    }
  };

  const canContinue =
    (() => {
      switch (currentStep) {
        case "welcome":
          return true;
        case "method":
          return false;
        case "integration":
          return true;
        case "file":
          return selectedFile.length > 0;
        case "mapping":
          return requiredMappingsComplete;
        case "location":
        case "practitioner":
        case "patient":
          return manualStepCanContinue(currentStep);
        case "preview":
          return path === "manual" || selectedRecordCount > 0;
        case "result":
          return true;
      }
    })() && !isSaving;

  const goBack = () => {
    if (stepIndex === 0 || currentStep === "result") {
      return;
    }

    if (stepIndex === 2) {
      setPath(null);
      setStepIndex(1);
      return;
    }

    setStepIndex(current => current - 1);
  };

  const goNext = async () => {
    if (!canContinue) {
      return;
    }

    if (currentStep === "integration" || currentStep === "result") {
      onClose();
      return;
    }

    if (currentStep === "preview" && path === "manual") {
      setIsSaving(true);
      setSaveError(false);
      try {
        await onSubmitManualSetup({
          locations: filledLocationDrafts,
          patients: filledPatientDrafts,
          practitioners: filledPractitionerDrafts,
        });
      } catch (error) {
        captureException(error, "onboarding: complete manual setup");
        setSaveError(true);
        return;
      } finally {
        setIsSaving(false);
      }
    }

    setStepIndex(current => current + 1);
  };

  const updateMapping = (fieldKey: keyof FieldMappings, sourceColumn: string) => {
    setMappings(current => (current ? { ...current, [fieldKey]: sourceColumn } : current));
  };

  const toggleRecord = (recordId: string) => {
    setSelectedRecordIds(current => {
      const updated = new Set(current);
      if (updated.has(recordId)) {
        updated.delete(recordId);
      } else {
        updated.add(recordId);
      }
      return updated;
    });
  };

  const stepNumber = stepIndex >= 2 && currentStep !== "result" ? stepIndex - 1 : null;
  const stepTotal = path ? sequence.length - (lastStepIsResult ? 3 : 2) : 0;
  const manualStepSummary = (resource: CreateResource) => {
    if (manualDraftCounts[resource] === 0) {
      return null;
    }
    const parts = [translate(`onboarding.manual.${resource}.count`, { smart_count: manualDraftCounts[resource] })];
    if (resource === "location" && locationTotals.wards > 0) {
      parts.push(translate("onboarding.manual.location.wardCount", { smart_count: locationTotals.wards }));
    }
    return parts.join(" · ");
  };
  const manualNextLabel = (resource: CreateResource) => {
    const summary = manualStepSummary(resource);
    return summary ? translate("onboarding.manual.next", { summary }) : translate("onboarding.navigation.next");
  };
  const nextLabel =
    currentStep === "welcome"
      ? translate("onboarding.wizard.start")
      : currentStep === "method"
        ? translate("onboarding.wizard.chooseMethod")
        : currentStep === "integration"
          ? translate("onboarding.navigation.close")
          : currentStep === "preview" && path !== "manual"
            ? translate("onboarding.preview.startSimulation")
            : currentStep === "preview"
              ? translate("onboarding.preview.finishManual")
              : currentStep === "result"
                ? translate("onboarding.navigation.close")
                : currentStep === "location" || currentStep === "practitioner" || currentStep === "patient"
                  ? manualNextLabel(currentStep)
                  : translate("onboarding.navigation.next");

  const selectedCounts = {
    location: countRecords(selectedRecordIds, "location"),
    practitioner: countRecords(selectedRecordIds, "practitioner"),
    patient: countRecords(selectedRecordIds, "patient"),
  };

  const renderWelcome = () => (
    <Stack spacing={space.sm}>
      <Box>
        <Typography variant="h4">{translate("onboarding.welcome.title")}</Typography>
        <Typography color="text.secondary" sx={{ mt: space.xs }}>
          {translate("onboarding.welcome.description")}
        </Typography>
      </Box>
      <Alert severity="info">{translate("onboarding.demo.description")}</Alert>
      <Box
        sx={{
          display: "grid",
          gap: space.sm,
          gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
        }}
      >
        {["choose", "review", "start"].map((intro, index) => (
          <Box key={intro} sx={{ display: "flex", gap: space.xs }}>
            <Box
              sx={{
                alignItems: "center",
                backgroundColor: color.selectedOptionBackground,
                borderRadius: borderRadius.lg,
                color: color.primary,
                display: "flex",
                flexShrink: 0,
                height: 32,
                justifyContent: "center",
                width: 32,
              }}
            >
              {index + 1}
            </Box>
            <Box>
              <Typography fontWeight="medium">{translate(`onboarding.welcome.${intro}.title`)}</Typography>
              <Typography color="text.secondary" variant="body2">
                {translate(`onboarding.welcome.${intro}.description`)}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Stack>
  );

  const renderMethod = () => (
    <Stack spacing={space.sm}>
      <Box>
        <Typography variant="h5">{translate("onboarding.method.title")}</Typography>
        <Typography color="text.secondary" sx={{ mt: space.xxs }}>
          {translate("onboarding.method.description")}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "grid",
          gap: space.xs,
          gridTemplateColumns: { xs: "1fr", lg: "repeat(3, 1fr)" },
        }}
      >
        <MethodCard
          action={translate("onboarding.method.integration.action")}
          description={translate("onboarding.method.integration.description")}
          icon={<CloudSync />}
          onSelect={() => selectPath("integration")}
          points={[
            translate("onboarding.method.integration.point1"),
            translate("onboarding.method.integration.point2"),
            translate("onboarding.method.integration.point3"),
          ]}
          title={translate("onboarding.method.integration.title")}
        />
        <MethodCard
          action={translate("onboarding.method.file.action")}
          description={translate("onboarding.method.file.description")}
          icon={<Description />}
          onSelect={() => selectPath("file")}
          points={[
            translate("onboarding.method.file.point1"),
            translate("onboarding.method.file.point2"),
            translate("onboarding.method.file.point3"),
          ]}
          title={translate("onboarding.method.file.title")}
        />
        <MethodCard
          action={translate("onboarding.method.manual.action")}
          description={translate("onboarding.method.manual.description")}
          icon={<EditNote />}
          onSelect={() => selectPath("manual")}
          points={[
            translate("onboarding.method.manual.point1"),
            translate("onboarding.method.manual.point2"),
            translate("onboarding.method.manual.point3"),
          ]}
          title={translate("onboarding.method.manual.title")}
        />
      </Box>
    </Stack>
  );

  const renderIntegration = () => (
    <Stack spacing={space.sm}>
      <Box>
        <Typography variant="h5">{translate("onboarding.integration.title")}</Typography>
        <Typography color="text.secondary" sx={{ mt: space.xxs }}>
          {translate("onboarding.integration.description")}
        </Typography>
      </Box>
      <Alert severity="warning">{translate("onboarding.integration.realDataNotice")}</Alert>
      <IntegrationSettings />
    </Stack>
  );

  const renderFile = () => (
    <Stack spacing={space.sm}>
      <Box>
        <Typography variant="h5">{translate("onboarding.file.title")}</Typography>
        <Typography color="text.secondary" sx={{ mt: space.xxs }}>
          {translate("onboarding.file.description")}
        </Typography>
      </Box>
      <Alert severity="info">{translate("onboarding.file.simulationNotice")}</Alert>
      <Box
        sx={{
          alignItems: "center",
          backgroundColor: color.background2,
          border: `2px dashed ${color.selectedOptionBorder}`,
          borderRadius: borderRadius.md,
          display: "flex",
          flexDirection: "column",
          gap: space.xs,
          p: space.lg,
          textAlign: "center",
        }}
      >
        <CloudUpload color="primary" fontSize="large" />
        <Typography variant="h6">{translate("onboarding.file.dropTitle")}</Typography>
        <Typography color="text.secondary" variant="body2">
          {translate("onboarding.file.formats")}
        </Typography>
        <Button onClick={() => setSelectedFile("stammdaten_export_2026-08-07.csv")} variant="contained">
          {translate("onboarding.file.selectDemo")}
        </Button>
      </Box>
      {selectedFile && (
        <Alert icon={<CheckCircle />} severity="success">
          <Typography fontWeight="medium">{selectedFile}</Typography>
          <Typography variant="body2">{translate("onboarding.file.detected")}</Typography>
        </Alert>
      )}
    </Stack>
  );

  const renderMapping = () => {
    if (!mappings || path !== "file") {
      return null;
    }

    const sourceColumns = mappingFields.map(field => field.fileColumn);
    return (
      <Stack spacing={space.sm}>
        <Box>
          <Typography variant="h5">{translate("onboarding.mapping.title")}</Typography>
          <Typography color="text.secondary" sx={{ mt: space.xxs }}>
            {translate("onboarding.mapping.fileDescription")}
          </Typography>
        </Box>
        <Alert severity="info">{translate("onboarding.mapping.simulationNotice")}</Alert>
        <Box sx={{ alignItems: "center", display: "flex", gap: space.xs, justifyContent: "space-between" }}>
          <Typography fontWeight="medium">{translate("onboarding.mapping.status")}</Typography>
          <Chip
            color={requiredMappingsComplete ? "success" : "warning"}
            label={translate("onboarding.mapping.mappedCount", {
              count: mappedFieldCount,
              total: mappingFields.length,
            })}
          />
        </Box>
        <TableContainer sx={{ border: `1px solid ${color.panelBorder}`, borderRadius: borderRadius.md }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{translate("onboarding.mapping.portalField")}</TableCell>
                <TableCell>{translate("onboarding.mapping.sample")}</TableCell>
                <TableCell>{translate("onboarding.mapping.sourceColumn")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mappingFields.map(field => (
                <TableRow key={field.key}>
                  <TableCell>
                    <Typography fontWeight="medium">
                      {translate(field.labelKey)}
                      {field.required ? " *" : ""}
                    </Typography>
                  </TableCell>
                  <TableCell>{field.sample}</TableCell>
                  <TableCell sx={{ minWidth: 240 }}>
                    <TextField
                      fullWidth
                      label={translate(field.labelKey)}
                      onChange={event => updateMapping(field.key, event.target.value)}
                      select
                      SelectProps={{ native: true }}
                      size="small"
                      value={mappings[field.key]}
                    >
                      <option value="">{translate("onboarding.mapping.skip")}</option>
                      {sourceColumns.map(sourceColumn => (
                        <option key={sourceColumn} value={sourceColumn}>
                          {sourceColumn}
                        </option>
                      ))}
                    </TextField>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {!requiredMappingsComplete && <Alert severity="warning">{translate("onboarding.mapping.required")}</Alert>}
      </Stack>
    );
  };

  const renderManualStep = (resource: CreateResource) => {
    return (
      <Box>
        {resource === "location" && (
          <ManualLocationsEditor
            disabled={isSaving}
            drafts={locationDrafts}
            existingRecords={existingRecords.location}
            onChange={setLocationDrafts}
          />
        )}
        {resource === "practitioner" && (
          <ManualPractitionersEditor
            disabled={isSaving}
            drafts={practitionerDrafts}
            existingRecords={existingRecords.practitioner}
            locationChoices={availableLocationChoices}
            onChange={setPractitionerDrafts}
          />
        )}
        {resource === "patient" && (
          <ManualPatientsEditor
            disabled={isSaving}
            drafts={patientDrafts}
            existingRecords={existingRecords.patient}
            locationChoices={availableLocationChoices}
            onChange={setPatientDrafts}
          />
        )}
      </Box>
    );
  };

  const renderPreview = () => {
    if (path === "manual") {
      return (
        <Stack spacing={space.sm}>
          <Box>
            <Typography variant="h5">{translate("onboarding.preview.manualTitle")}</Typography>
            <Typography color="text.secondary" sx={{ mt: space.xxs }}>
              {translate("onboarding.preview.manualDescription")}
            </Typography>
          </Box>
          <Stack spacing={space.xxs + 0.25}>
            {manualResources.map(({ icon, resource }) => {
              const summary = manualStepSummary(resource);
              const staged = summary !== null;
              const resourceTitle = translate(`onboarding.manual.${resource}.statusTitle`);
              return (
                <Box
                  data-testid={`manual-preview-${resource}`}
                  key={resource}
                  sx={{
                    alignItems: "center",
                    backgroundColor: color.background2,
                    border: `1px solid ${color.panelBorder}`,
                    borderRadius: borderRadius.md + 6,
                    display: "flex",
                    gap: space.xs,
                    px: 2.25,
                    py: 1.75,
                  }}
                >
                  <Box
                    sx={{
                      alignItems: "center",
                      backgroundColor: staged ? color.plannedChipBackground : sessionStatusStyle.pending.bg,
                      borderRadius: borderRadius.md + 4,
                      color: staged ? color.plannedText : sessionStatusStyle.pending.color,
                      display: "flex",
                      flexShrink: 0,
                      height: 40,
                      justifyContent: "center",
                      width: 40,
                    }}
                  >
                    {icon}
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                    <Typography sx={uppercaseLabelSx}>{resourceTitle}</Typography>
                    <Typography color={staged ? "text.primary" : "text.secondary"} fontWeight="medium">
                      {summary ?? translate("onboarding.preview.noneStaged")}
                    </Typography>
                    <Typography color="text.secondary" variant="body2">
                      {translate("onboarding.preview.existingCount", { count: manualStepCounts[resource] })}
                    </Typography>
                  </Box>
                  <Button
                    aria-label={translate("onboarding.preview.editResource", { resource: resourceTitle })}
                    disabled={isSaving}
                    onClick={() => setStepIndex(sequence.indexOf(resource))}
                    size="small"
                    startIcon={<Edit />}
                    sx={{ backgroundColor: color.white, flexShrink: 0, ml: "auto" }}
                    variant="outlined"
                  >
                    {translate("onboarding.preview.edit")}
                  </Button>
                </Box>
              );
            })}
          </Stack>
          <Alert severity="info">{translate("onboarding.preview.manualReady")}</Alert>
          {saveError && <Alert severity="error">{translate("onboarding.manual.saveError")}</Alert>}
        </Stack>
      );
    }

    return (
      <Stack spacing={space.sm}>
        <Box>
          <Typography variant="h5">{translate("onboarding.preview.title")}</Typography>
          <Typography color="text.secondary" sx={{ mt: space.xxs }}>
            {translate("onboarding.preview.description")}
          </Typography>
        </Box>
        <Alert severity="info">{translate("onboarding.preview.simulationNotice")}</Alert>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: space.xs }}>
          <Chip label={translate("onboarding.preview.selectedCount", { count: selectedRecordCount })} />
          <Chip label={translate("onboarding.preview.locationCount", { count: selectedCounts.location })} />
          <Chip label={translate("onboarding.preview.practitionerCount", { count: selectedCounts.practitioner })} />
          <Chip label={translate("onboarding.preview.patientCount", { count: selectedCounts.patient })} />
        </Box>
        <Box
          sx={{
            display: "grid",
            gap: space.xs,
            gridTemplateColumns: { xs: "1fr", lg: "repeat(3, 1fr)" },
          }}
        >
          {(["location", "practitioner", "patient"] as ImportRecordKind[]).map(kind => (
            <Card key={kind} variant="outlined" sx={{ borderColor: color.panelBorder, borderRadius: borderRadius.md }}>
              <CardContent>
                <Typography variant="h6">{translate(kindTranslationKeys[kind])}</Typography>
                <Stack spacing={space.xxs} sx={{ mt: space.xs }}>
                  {importPreviewRecords
                    .filter(record => record.kind === kind)
                    .map(record => (
                      <Box key={record.id} sx={{ alignItems: "flex-start", display: "flex" }}>
                        <Checkbox
                          checked={selectedRecordIds.has(record.id)}
                          inputProps={{
                            "aria-label": translate("onboarding.preview.toggleRecord", { name: record.primary }),
                          }}
                          onChange={() => toggleRecord(record.id)}
                          size="small"
                        />
                        <Box sx={{ minWidth: 0, pt: space.xxs }}>
                          <Typography fontWeight="medium" noWrap>
                            {record.primary}
                          </Typography>
                          <Typography color="text.secondary" noWrap variant="body2">
                            {record.secondary}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Stack>
    );
  };

  const renderResult = () => {
    const resultStats: ResultStat[] =
      path === "manual"
        ? [
            { color: color.primary, icon: <LocationOn />, key: "sites", value: manualResultCounts.location },
            { color: color.plannedText, icon: <HomeWork />, key: "wards", value: manualResultWardCount },
            { color: color.primary, icon: <Groups />, key: "practitioners", value: manualResultCounts.practitioner },
            {
              color: sessionStatusStyle.pending.color,
              icon: <Elderly />,
              key: "patients",
              value: manualResultCounts.patient,
            },
          ]
        : [
            { color: color.primary, icon: <LocationOn />, key: "locations", value: selectedCounts.location },
            { color: color.primary, icon: <Groups />, key: "practitioners", value: selectedCounts.practitioner },
            {
              color: sessionStatusStyle.pending.color,
              icon: <Elderly />,
              key: "patients",
              value: selectedCounts.patient,
            },
          ];
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        spacing={space.sm}
        sx={{ minHeight: "100%", py: space.sm, textAlign: "center" }}
      >
        <Box
          sx={{
            alignItems: "center",
            backgroundColor: color.plannedChipBackground,
            borderRadius: "50%",
            boxShadow: `0 0 0 10px ${color.plannedBackground}`,
            color: color.plannedText,
            display: "flex",
            height: 74,
            justifyContent: "center",
            width: 74,
          }}
        >
          <Check fontSize="large" />
        </Box>
        <Box sx={{ maxWidth: 720 }}>
          <Typography variant="h4">
            {translate(path === "manual" ? "onboarding.result.manualTitle" : "onboarding.result.simulationTitle")}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: space.xs }}>
            {translate(
              path === "manual" ? "onboarding.result.manualDescription" : "onboarding.result.simulationDescription"
            )}
          </Typography>
        </Box>
        {path !== "manual" && <Alert severity="info">{translate("onboarding.result.notPersisted")}</Alert>}
        <Box
          data-testid="onboarding-result-stats"
          sx={{
            alignItems: "center",
            backgroundColor: color.white,
            border: `1px solid ${color.panelBorder}`,
            borderRadius: borderRadius.md + 6,
            columnGap: space.sm - 1,
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            px: 2.5,
            py: 1.5,
            rowGap: space.xxs,
          }}
        >
          {resultStats.map(stat => (
            <Typography
              key={stat.key}
              sx={{ alignItems: "center", display: "flex", gap: space.xxs, whiteSpace: "nowrap" }}
            >
              <Box component="span" sx={{ color: stat.color, display: "flex" }}>
                {stat.icon}
              </Box>
              <Box component="span" sx={{ fontWeight: typography.fontWeight.semibold }}>
                {stat.value}
              </Box>{" "}
              <Box component="span" sx={{ color: "text.secondary" }}>
                {translate(`onboarding.result.stats.${stat.key}`, { smart_count: stat.value })}
              </Box>
            </Typography>
          ))}
        </Box>
        <ButtonBase
          data-testid="onboarding-next-step"
          onClick={onOpenDeviceEnrollment}
          sx={{
            "&:hover": { backgroundColor: color.selectedOptionBackground, borderColor: color.primary },
            alignItems: "center",
            backgroundColor: color.background2,
            border: `1px solid ${color.selectedOptionBorder}`,
            borderRadius: borderRadius.md + 8,
            display: "flex",
            gap: space.xs,
            maxWidth: 460,
            px: 2.5,
            py: 2,
            textAlign: "left",
            width: "100%",
          }}
        >
          <Box
            sx={{
              alignItems: "center",
              backgroundColor: color.secondaryButtonBackground,
              borderRadius: borderRadius.md + 5,
              color: color.primary,
              display: "flex",
              flexShrink: 0,
              height: 42,
              justifyContent: "center",
              width: 42,
            }}
          >
            <TabletMac />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0 }}>
            <Typography sx={uppercaseLabelSx}>{translate("onboarding.result.nextStep")}</Typography>
            <Typography fontWeight="medium">{translate("onboarding.result.connectDevices")}</Typography>
            <Typography color="text.secondary" variant="body2">
              {translate("onboarding.result.connectDevicesHint")}
            </Typography>
          </Box>
          <ArrowForward sx={{ color: color.primary, flexShrink: 0, ml: "auto" }} />
        </ButtonBase>
      </Stack>
    );
  };

  const stepContent: Record<WizardStep, () => ReactNode> = {
    welcome: renderWelcome,
    method: renderMethod,
    integration: renderIntegration,
    file: renderFile,
    mapping: renderMapping,
    location: () => renderManualStep("location"),
    practitioner: () => renderManualStep("practitioner"),
    patient: () => renderManualStep("patient"),
    preview: renderPreview,
    result: renderResult,
  };

  return (
    <Dialog
      aria-labelledby="onboarding-wizard-title"
      data-testid="onboarding-wizard"
      fullWidth
      maxWidth={false}
      onClose={isSaving ? undefined : onClose}
      open={open}
      PaperProps={{
        sx: {
          borderRadius: borderRadius.md,
          boxShadow: shadows.dialog,
          height: "calc(100% - 28px)",
          m: 0,
          maxHeight: "none",
          maxWidth: "none",
          overflow: "hidden",
          width: "calc(100% - 28px)",
        },
      }}
    >
      <Box sx={{ alignItems: "center", display: "flex", gap: space.xs, px: space.sm, py: space.xs }}>
        <Box
          sx={{
            alignItems: "center",
            backgroundColor: color.selectedOptionBackground,
            borderRadius: borderRadius.md,
            color: color.primary,
            display: "flex",
            flexShrink: 0,
            height: 40,
            justifyContent: "center",
            width: 40,
          }}
        >
          <RocketLaunch />
        </Box>
        <Box>
          <Box sx={{ alignItems: "center", display: "flex", gap: space.xs }}>
            <Typography id="onboarding-wizard-title" variant="h6">
              {translate("onboarding.wizard.title")}
            </Typography>
            {path === "file" && <Chip label={translate("onboarding.demo.label")} size="small" variant="outlined" />}
          </Box>
          {stepNumber !== null && (
            <Typography color="text.secondary" variant="body2">
              {translate("onboarding.wizard.step", { current: stepNumber, total: stepTotal })}
            </Typography>
          )}
        </Box>
        <IconButton
          aria-label={translate("onboarding.navigation.close")}
          disabled={isSaving}
          onClick={onClose}
          sx={{ backgroundColor: color.background2, ml: "auto" }}
        >
          <Close />
        </IconButton>
      </Box>

      <ProgressStrip currentStep={currentStep} sequence={sequence} />

      <DialogContent sx={{ minHeight: 0, p: space.sm }}>{stepContent[currentStep]()}</DialogContent>

      <DialogActions
        sx={{
          backgroundColor: color.background2,
          borderTop: `1px solid ${color.panelBorder}`,
          justifyContent: "space-between",
          px: space.sm,
          py: space.xs,
        }}
      >
        <Button
          disabled={isSaving || stepIndex === 0 || currentStep === "result"}
          onClick={goBack}
          startIcon={<ArrowBack />}
          variant="outlined"
        >
          {translate("onboarding.navigation.back")}
        </Button>
        <Button
          disabled={!canContinue}
          endIcon={isSaving || currentStep === "integration" || currentStep === "result" ? undefined : <ArrowForward />}
          onClick={() => void goNext()}
          startIcon={isSaving ? <CircularProgress color="inherit" size={18} /> : undefined}
          variant="contained"
        >
          {nextLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
